import React from 'react';
import { useWindowSize } from 'react-use';
import { screenIsSmall } from 'utils/screen-is-small';
import { Card, Icon } from '@perch-labs/shrike';
import { Notification } from 'components/notification';
import {
  NotificationKey,
  isHighError,
  isLow,
  isWarning,
} from 'domain/notifications';
import { Container } from 'styles';
import { NotificationSVG } from '../notification-svg';
import {
  ErrorTypeNotificationCard,
  StyledContainer,
  WarningTypeNotificationCard,
} from './notification-card.style';

interface Props {
  notification: QueriedNotification;
  margin?: Margin;
  isSMB: boolean;
}

export const NotificationCard = ({
  notification,
  margin = { bottom: '24px' },
  isSMB,
}: Props) => {
  const { width } = useWindowSize();
  const { priorityType, notificationKey, utilityAccountDetails } = notification;
  const { accountNumber, displayServiceAddress, utilityDisplayName } =
    utilityAccountDetails || {};
  const shouldShowSubtitle: boolean =
    isSMB &&
    (!!accountNumber || !!displayServiceAddress || !!utilityDisplayName);
  const messageProps = !screenIsSmall(width)
    ? {
        margin: { top: '16px' },
      }
    : undefined;

  if (notificationKey === NotificationKey.lowIncomeHousehold) {
    return (
      <Card
        mobileVariant="flat"
        margin={margin}
        data-testid="notification-card"
      >
        <StyledContainer
          flex
          padding="24px"
          small={{ direction: 'column', padding: '24px' }}
        >
          <NotificationSVG notificationKey={notification.notificationKey} />
          <Notification
            notification={notification}
            messageProps={messageProps}
            titleProps={{ textStyle: 'heading700' }}
            showDismissButton={true}
            showDate={false}
            shouldShowSubtitle={shouldShowSubtitle}
          />
        </StyledContainer>
      </Card>
    );
  }

  if (isHighError(priorityType)) {
    return (
      <ErrorTypeNotificationCard
        mobileVariant="flat"
        margin={margin}
        data-testid="error-type-notification-card"
      >
        <Container
          flex
          padding="24px"
          small={{ padding: '24px 24px 24px 16px' }}
        >
          <div>
            <Icon
              icon="ExclamationCircleInverted"
              color="errorBright"
              margin={{ right: '16px', top: '5px' }}
              aria-hidden={true}
            />
          </div>
          <Notification
            notification={notification}
            titleProps={{ color: 'error', textStyle: 'heading700' }}
            messageProps={messageProps}
            showDate={false}
            buttonLinkProps={{ margin: { top: '16px' } }}
            shouldShowSubtitle={shouldShowSubtitle}
          />
        </Container>
      </ErrorTypeNotificationCard>
    );
  } else if (isWarning(priorityType)) {
    return (
      <WarningTypeNotificationCard
        mobileVariant="flat"
        margin={margin}
        data-testid="warning-type-notification-card"
      >
        <Container
          flex
          padding="24px"
          small={{ padding: '24px 24px 24px 16px' }}
        >
          <div>
            <Icon
              icon="ExclamationCircleInverted"
              color="warningBright"
              margin={{ right: '16px' }}
              aria-hidden={true}
            />
          </div>
          <Notification
            notification={notification}
            titleProps={{ color: 'warningBright', textStyle: 'heading700' }}
            messageProps={messageProps}
            showDate={false}
            buttonLinkProps={{ margin: { top: '24px' } }}
            showDismissButton={true}
            shouldShowSubtitle={shouldShowSubtitle}
          />
        </Container>
      </WarningTypeNotificationCard>
    );
  }

  return (
    <Card mobileVariant="flat" margin={margin} data-testid="notification-card">
      <StyledContainer
        flex
        padding="24px"
        small={{ direction: 'column', padding: '24px' }}
      >
        <NotificationSVG notificationKey={notification.notificationKey} />
        <Notification
          notification={notification}
          messageProps={messageProps}
          titleProps={{ textStyle: 'heading700' }}
          showDismissButton={isLow(priorityType)}
          showDate={false}
          shouldShowSubtitle={shouldShowSubtitle}
        />
      </StyledContainer>
    </Card>
  );
};
